import { useState, useMemo, type FC } from "react";
import { Search, ChevronRight, Folder, File, ChevronDown, Upload } from "lucide-react";
import { format } from "date-fns";

import { cn } from "@/shared/utils/core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useDocuments } from "@/shared/hooks/useDocumentStorage";
import { useFolders } from "@/shared/hooks/useDocumentFolders";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";

import type { DocumentFolder } from "@/shared/types/folderTypes";

interface ChooseReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (documentId: number) => void;
  onUploadInstead: () => void;
  transactionDescription: string;
  transactionAmount: string;
  transactionDate: string;
}

type VaultDocument = NonNullable<ReturnType<typeof useDocuments>["data"]>["documents"][number];

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "payment_receipt", label: "Receipt" },
  { value: "bank_statement", label: "Bank statement" },
  { value: "funeral_invoice", label: "Funeral invoice" },
  { value: "tax_document", label: "Tax document" },
  { value: "other", label: "Other" },
] as const;

export const ChooseReceiptDialog: FC<ChooseReceiptDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
  onUploadInstead,
  transactionDescription,
  transactionAmount,
  transactionDate,
}) => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [expandedFolderId, setExpandedFolderId] = useState<number | null>(null);

  const { data: docData, error: docsError } = useDocuments();
  const { data: folderData } = useFolders();

  const documents = docData?.documents ?? [];
  const folders = folderData?.folders ?? [];

  const filtered = useMemo(() => {
    let docs = documents;
    if (typeFilter !== "all") {
      docs = docs.filter((d) => d.document_type === typeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      docs = docs.filter(
        (d) =>
          (d.display_name ?? d.file_name).toLowerCase().includes(q) ||
          (d.type_label ?? "").toLowerCase().includes(q),
      );
    }
    return docs;
  }, [documents, search, typeFilter]);

  const isSearching = search.trim() !== "" || typeFilter !== "all";
  const unfolderedDocs = useMemo(
    () => isSearching ? filtered : filtered.filter((d) => !d.folder_id),
    [filtered, isSearching],
  );

  const foldersWithDocs = useMemo(() => {
    if (search.trim() || typeFilter !== "all") {
      return [] as (DocumentFolder & { docs: VaultDocument[] })[];
    }
    return folders
      .map((f) => ({
        ...f,
        docs: documents.filter((d) => d.folder_id === f.id),
      }))
      .filter((f) => f.docs.length > 0);
  }, [folders, documents, search, typeFilter]);

  const handleAttach = () => {
    if (selectedDocId) {
      onSelect(selectedDocId);
      setSelectedDocId(null);
      setSearch("");
      setTypeFilter("all");
    }
  };

  const handleClose = (o: boolean) => {
    if (!o) {
      setSelectedDocId(null);
      setSearch("");
      setTypeFilter("all");
      setExpandedFolderId(null);
    }
    onOpenChange(o);
  };

  const typeFilterLabel = TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label ?? "All types";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[640px] p-0 gap-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Choose a receipt</DialogTitle>
          <p className="text-sm text-secondary">
            For {transactionDescription} · {transactionAmount} · {transactionDate}
          </p>
        </DialogHeader>

        <div className="flex items-center gap-3 px-6 pb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents and folders"
              className="pl-9"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-10 shrink-0 items-center gap-2 rounded-sm border border-primary bg-white px-3 text-sm"
              >
                {typeFilterLabel}
                <ChevronDown className="h-3 w-3 text-secondary" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.value} onSelect={() => setTypeFilter(opt.value)}>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex-1 overflow-y-auto border-t border-primary">
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_140px_100px_24px] gap-2 border-b border-primary px-6 py-2 text-xs font-medium text-secondary">
            <span>Name</span>
            <span>Type</span>
            <span>Updated</span>
            <span />
          </div>

          {/* Folders */}
          {foldersWithDocs.map((folder) => (
            <div key={folder.id}>
              <button
                type="button"
                onClick={() => setExpandedFolderId(expandedFolderId === folder.id ? null : folder.id)}
                className="grid w-full grid-cols-[1fr_140px_100px_24px] gap-2 items-center px-6 py-3 text-left hover:bg-subtle transition-colors"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Folder className="h-4 w-4 shrink-0 text-secondary" />
                  <span className="text-sm font-medium truncate">{folder.name}</span>
                  <span className="text-xs text-secondary shrink-0">{folder.file_count} files</span>
                </span>
                <span className="text-xs text-secondary truncate">
                  {folder.document_types.length === 1
                    ? getDocumentTypeLabel(folder.document_types[0])
                    : "Mixed"}
                </span>
                <span className="text-xs text-secondary">
                  {format(new Date(folder.latest_upload_date), "M/d/yyyy")}
                </span>
                <ChevronRight
                  className={cn(
                    "h-4 w-4 text-secondary transition-transform",
                    expandedFolderId === folder.id && "rotate-90",
                  )}
                />
              </button>
              {expandedFolderId === folder.id &&
                folder.docs.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    selected={selectedDocId === doc.id}
                    onSelect={() => setSelectedDocId(doc.id)}
                    indented
                  />
                ))}
            </div>
          ))}

          {/* Unfoldered documents */}
          {unfolderedDocs.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              selected={selectedDocId === doc.id}
              onSelect={() => setSelectedDocId(doc.id)}
            />
          ))}

          {docsError ? (
            <p className="px-6 py-8 text-center text-sm text-red-600">Couldn't load documents</p>
          ) : filtered.length === 0 && foldersWithDocs.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-secondary">No documents found</p>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-primary px-6 py-4">
          <button
            type="button"
            onClick={() => {
              handleClose(false);
              onUploadInstead();
            }}
            className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
          >
            <Upload className="h-4 w-4" />
            Upload a new file instead
          </button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button variant="brand" disabled={!selectedDocId} onClick={handleAttach}>
              Attach receipt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DocumentRow: FC<{
  doc: VaultDocument;
  selected: boolean;
  onSelect: () => void;
  indented?: boolean;
}> = ({ doc, selected, onSelect, indented }) => {
  const name = doc.display_name ?? doc.file_name;
  const typeLabel = doc.type_label ?? getDocumentTypeLabel(doc.document_type);
  const date = format(new Date(doc.updated_at || doc.created_at), "M/d/yyyy");

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "grid w-full grid-cols-[1fr_140px_100px_24px] gap-2 items-center px-6 py-3 text-left transition-colors",
        selected ? "bg-green-50" : "hover:bg-subtle",
        indented && "pl-12",
      )}
    >
      <span className="flex items-center gap-2 min-w-0">
        <span className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
          selected ? "border-brand bg-brand" : "border-stone-300",
        )}>
          {selected ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
        </span>
        <File className="h-4 w-4 shrink-0 text-stone-300" strokeWidth={1.5} />
        <span className="text-sm truncate">{name}</span>
      </span>
      <span className="text-xs text-secondary truncate">{typeLabel}</span>
      <span className="text-xs text-secondary">{date}</span>
      <span />
    </button>
  );
};
